import { cva, type VariantProps } from "class-variance-authority";
import clsx from "clsx";
import {
  type ForwardedRef,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import TextareaAutosize, {
  type TextareaAutosizeProps,
} from "react-textarea-autosize";

import { getWhitelabelCustomization } from "common/helpers/utils";

const textAreaVariants = cva(
  [
    "relative resize-none rounded-3xl border border-black-100 bg-black-100 text-black-950 transition-[background,border] scrollbar-thin",
    //placeholder
    "placeholder:text-black-500",
    //hover
    "hover:border-black-200 hover:bg-black-200",
    //disabled
    "disabled:cursor-not-allowed disabled:text-black-400 disabled:placeholder:text-black-400 disabled:hover:border-black-100 disabled:hover:bg-black-100",
    //focus
    "focus-visible:bg-white focus-visible:hover:bg-white focus-visible:outline-none",
    //error
    "data-[error=true]:!border-red-400",
    //error + focus
    "data-[error=true]:focus-visible:outline-red-300 data-[error=true]:focus-visible:outline-solid data-[error=true]:focus-visible:outline-2",
  ],
  {
    variants: {
      variant: {
        sm: "rounded-xl p-2 text-xs-medium",
        md: "rounded-2xl p-2.5 text-sm-medium",
        lg: "rounded-3xl p-3 text-md-medium",
      },
      hasWhitelabelColor: {
        true: "focus-visible:border-whitelabel-primary focus-visible:hover:border-whitelabel-primary",
        false: "focus-visible:border-purple-400",
      },
    },
    defaultVariants: {
      variant: "md",
    },
  },
);

interface TextAreaProps
  extends TextareaAutosizeProps,
    VariantProps<typeof textAreaVariants> {
  hasError?: boolean;
}

function TextArea(
  { hasError = null, variant, className, ...props }: TextAreaProps,
  ref: ForwardedRef<HTMLTextAreaElement>,
) {
  // Create a ref for the textarea
  const localRef = useRef<HTMLTextAreaElement>(null);

  // Sync the forwarded ref with the local ref
  useImperativeHandle(ref, () => localRef.current);

  return (
    <TextareaAutosize
      className={clsx(
        textAreaVariants({
          variant,
          className,
          hasWhitelabelColor: !!getWhitelabelCustomization()?.primary_color,
        }),
      )}
      ref={localRef}
      {...props}
      data-error={hasError}
    />
  );
}
const TextAreaWithRef = forwardRef(TextArea);
TextAreaWithRef.displayName = "TextArea";

export default TextAreaWithRef;
